// Smart Sash Admin Panel - Authentication Context
// React context for authentication state management

import { AuthUser, LoginRequest, ValidateSessionResponse } from '@/models';
import { authService, userService } from '@/services/api';
import { handleApiError, isAxiosError } from '@/services/http';
import { useUIStore } from '@/store/uiStore';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionValidationService } from './sessionValidationService';
import { tokenManager } from './tokenManager';

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  requiresPasswordChange: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  completePasswordChange: (oldPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  syncAuthState: () => Promise<void>;
  validateSession: (sessionId: string) => Promise<ValidateSessionResponse>;
}

type AuthErrorContext = 'login' | 'session' | 'refresh' | 'passwordChange' | 'logout';

const getAuthErrorMessage = (err: unknown, context: AuthErrorContext): string => {
  const apiError = handleApiError(err, `Auth:${context}`);
  if (isAxiosError(err) && err.response?.status === 401) {
    switch (context) {
      case 'login':
        return 'Invalid Username or Password. Please check your credentials and try again.';
      case 'session':
      case 'refresh':
        return 'Your session has expired. Please log in again.';
      case 'passwordChange':
        return 'Your current password is incorrect.';
      default:
        return apiError.message;
    }
  }
  return apiError.message;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { addNotification } = useUIStore();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(
    tokenManager.requiresPasswordChange()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = user !== null;

  const clearLocalAuthState = useCallback(() => {
    setUser(null);
    setRequiresPasswordChange(false);
  }, []);

  /**
   * Sync auth state by asking the server if a valid session exists.
   * Called on app mount and after operations that change auth state.
   */
  const syncAuthState = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const passwordChangeRequired = tokenManager.requiresPasswordChange();
      setRequiresPasswordChange(passwordChangeRequired);

      if (passwordChangeRequired) {
        setUser(null);
        return;
      }

      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      setError(getAuthErrorMessage(err, 'session'));
      clearLocalAuthState();
    } finally {
      setIsLoading(false);
    }
  }, [clearLocalAuthState]);

  // Initialize auth state on mount via GET /auth/me.
  useEffect(() => {
    void syncAuthState();
  }, [syncAuthState]);

  // Listen for session-expiry events dispatched by the 401 interceptor.
  useEffect(() => {
    const handleSessionExpired = () => {
      clearLocalAuthState();
      addNotification({
        type: 'warning',
        message: 'Your session has expired. Please log in again.',
        duration: 6000,
      });
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [clearLocalAuthState, addNotification, navigate]);

  /**
   * Login with credentials.
   * The server sets HttpOnly cookies; we call /auth/me to get user info.
   */
  const login = async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const { requiresPasswordChange: passwordChangeRequired } =
        await authService.login(credentials);

      tokenManager.setPasswordChangeRequired(passwordChangeRequired);
      setRequiresPasswordChange(passwordChangeRequired);

      if (passwordChangeRequired) {
        setUser(null);
        return;
      }

      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      setError(getAuthErrorMessage(err, 'login'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete required password change and finish authentication.
   */
  const completePasswordChange = async (oldPassword: string, newPassword: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await userService.changePassword({ oldPassword, newPassword });

      tokenManager.setPasswordChangeRequired(false);
      await syncAuthState();
    } catch (err) {
      setError(getAuthErrorMessage(err, 'passwordChange'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user — server clears the auth cookies.
   */
  const logout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.logout();
    } catch (err) {
      setError(getAuthErrorMessage(err, 'logout'));
    } finally {
      clearLocalAuthState();
      setIsLoading(false);
    }
  };

  /**
   * Re-sync auth state (e.g. after a forced refresh by external code).
   * The interceptor handles automatic token refresh; this is for explicit callers.
   */
  const refreshAuth = async () => {
    await syncAuthState();
  };

  /**
   * Validate registration session
   */
  const validateSession = async (sessionId: string): Promise<ValidateSessionResponse> => {
    return sessionValidationService.validateSession(sessionId);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    requiresPasswordChange,
    isLoading,
    error,
    login,
    completePasswordChange,
    logout,
    refreshAuth,
    syncAuthState,
    validateSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
